// MACHINERY V2 (Stroje) — catalog browse grid, empty state and loading
// skeleton (M2.8). Split out of CatalogBrowseV2 for the 300-line cap.
//
// Spec: data package §11 "States" — Empty = `noModelsMatchFilters`
// (+Secondary) + Clear when filters are active, `noModelsFound` otherwise.
// Artboards 0900 / 0901 draw the 3-up grid.
//
// THE COLUMN COUNT IS `auto-fill`, AND M2.16c MEASURED IT RATHER THAN REPLACING
// IT. The mobile pass owns the below-1280 steps the package draws for the
// katalog (1 / 2 / 3 columns at 390 / 768 / 1024, the same ladder screenshots
// 05, 07 and 08 draw for fleet V1). `minmax(300px, 1fr)` inside the page's own
// horizontal padding already produces exactly that ladder — 362px of content at
// 390 fits one track, 740px at 768 fits two, 996px at 1024 fits three — so the
// track list is the same expression at every width. Only the GAP differs from
// the board: 14 on the desktop grid, 10 below 1280.
//
// ============================================================
//  >>> AND THAT GAP RULE WAS DEAD CODE UNTIL NOW (M2.16c review, item 1)
// ============================================================
// `.mv2-catalog-grid { gap: 10px }` in mobile/machinery-v2-mobile.scss has
// never once applied, because this component wrote `gap: 14` as an INLINE style
// at every width and an inline declaration outranks a stylesheet rule. The
// class half of `FleetGrid`'s pattern was copied; the INLINE-DROP half was not.
// FleetGrid.tsx:143-153 omits `display` / `grid-template-columns` / `gap` from
// its inline object below 1280 precisely so the sheet can supply them, and this
// file now does the same — which is why it reads the module's below-desktop
// query. ONE grid element per page (the skeleton renders through this same
// component), so that is one `matchMedia` listener, which is what
// useV2MediaQuery.ts asks for.
//
// >>> DESKTOP IS BYTE-IDENTICAL. <<< At >= 1280 the inline object is the one it
// always was — `repeat(auto-fill, minmax(300px, 1fr))` at `gap: 14` — and the
// class, which is still applied unconditionally, matches no rule up there.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, useV2MediaQuery } from "../useV2MediaQuery";

/**
 * "zobrazuje sa 1–9 z 6 044" — artboard 0900 puts it directly under the
 * category tabs, above the grid, so that is where it renders (not in the
 * pagination strip). Range from `browseTotalCount` + LOCAL page math, per §11.
 *
 * The artboard's trailing "· zoradené: relevancia" clause is deliberately NOT
 * shipped: there is no sort control on this surface and no endpoint contract
 * that states the ordering, so printing "sorted by relevance" would be an
 * unverified claim about server behaviour (process rule (a)).
 */
export const CatalogShowingLine: React.FC<{
  page: number;
  pageSize: number;
  totalCount: number;
}> = ({ page, pageSize, totalCount }) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  const lang = getCurrentLanguage();
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  return (
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        color: "var(--faint)",
        letterSpacing: "0.04em",
        marginBottom: 10,
        minWidth: 0,
      }}
    >
      {translate("myMachinery:showingResults", {
        from: from.toLocaleString(lang),
        to: to.toLocaleString(lang),
        total: totalCount.toLocaleString(lang),
      })}
    </div>
  );
};

export const CatalogGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const belowDesktop = useV2MediaQuery(V2_BELOW_DESKTOP);

  return (
    <div
      className="mv2-catalog-grid"
      style={
        belowDesktop
          ? // The three dropped declarations come from `.mv2-catalog-grid` in
            // mobile/machinery-v2-mobile.scss — see this file's header. What is
            // left is what the stylesheet does not need to own.
            { alignItems: "stretch", minWidth: 0 }
          : {
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 14,
              alignItems: "stretch",
              minWidth: 0,
            }
      }
    >
      {children}
    </div>
  );
};

export const CatalogSkeletonGrid: React.FC<{ cards?: number }> = ({ cards = 9 }) => (
  <CatalogGrid>
    {Array.from({ length: cards }, (_, i) => (
      <div
        key={i}
        aria-hidden
        style={{
          height: 196,
          borderRadius: 10,
          background: "var(--card-2)",
          border: "1px solid var(--border-soft)",
        }}
      />
    ))}
  </CatalogGrid>
);

export const CatalogEmpty: React.FC<{ hasFilters: boolean; onClear: () => void }> = ({
  hasFilters,
  onClear,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        padding: "56px 20px",
        borderRadius: 10,
        border: "1px dashed var(--border)",
        background: "var(--card)",
        textAlign: "center",
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-2)" }}>
        {translate(
          hasFilters ? "myMachinery:noModelsMatchFilters" : "myMachinery:noModelsFound"
        )}
      </div>
      {hasFilters && (
        <React.Fragment>
          <div style={{ fontSize: 12.5, color: "var(--muted)", maxWidth: 380 }}>
            {translate("myMachinery:noModelsMatchFiltersSecondary")}
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClear}
            style={{ marginTop: 4 }}
          >
            {translate("myMachinery:clearAll")}
          </button>
        </React.Fragment>
      )}
    </div>
  );
};
